import type { ReactNode } from 'react';
import type { JobLog, ProgressInfo } from '@/hooks/useBackgroundJob';

// Barras de progreso (una por cada progressId activo) + consola de logs en vivo — extraído de
// BackgroundJobProcessor para reusarlo en cualquier página que dispare un job en segundo plano
// vía useBackgroundJob.
export function JobProgressConsole({
  progress,
  logs,
  hasMore,
  isLoadingMore,
  onLoadMore,
  isProcessing = false,
  resumen,
}: {
  progress: Record<string, ProgressInfo>;
  logs: JobLog[];
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  isProcessing?: boolean;
  /** Bloque de métricas a mostrar arriba de la consola, cuando el proceso ya terminó. */
  resumen?: ReactNode;
}) {
  return (
    <>
      {/* BARRAS DE PROGRESO (Una por cada proceso activo) */}
      {Object.entries(progress).map(([pId, p]) => (
        <div
          key={pId}
          className="mb-6 p-4 rounded-xl bg-blue-50/50 border border-blue-100 animate-in fade-in slide-in-from-top-2 duration-500"
        >
          <div className="flex justify-between items-end mb-3">
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-blue-400 uppercase tracking-tight">Proceso</span>
                <span className="text-[10px] font-mono text-blue-300">ID: {pId}</span>
              </div>
              <span className="text-sm font-semibold text-blue-900 line-clamp-1">{p.description || 'Procesando...'}</span>
            </div>
            <span className="text-xs font-mono font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded">
              {Math.round((p.current / p.total) * 100) || 0}%
            </span>
          </div>
          <div className="w-full bg-blue-200/50 rounded-full h-2 overflow-hidden backdrop-blur-sm">
            <div
              className="bg-blue-600 h-2 shadow-[0_0_15px_rgba(37,99,235,0.4)] rounded-full transition-all duration-700 ease-out"
              style={{ width: `${Math.max(0, Math.min(100, (p.current / p.total) * 100))}%` }}
            ></div>
          </div>
          <div className="mt-2 flex justify-end">
            <span className="text-[10px] text-blue-400 font-medium">
              {p.current} de {p.total} pasos completados
            </span>
          </div>
        </div>
      ))}

      {/* CONSOLA DE LOGS */}
      <div className="mt-4 flex flex-col">
        <div className="flex justify-between items-end mb-2 px-1">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Terminal de Eventos</h3>
          <span className="text-[10px] text-gray-400 font-mono">{logs.length} entradas cargadas</span>
        </div>

        {resumen}
        <div className="relative group">
          <div className="border border-gray-200 rounded-xl h-125 overflow-y-auto bg-gray-50 text-gray-800 p-5 font-mono text-[13px] leading-relaxed shadow-inner flex flex-col scrollbar-thin scrollbar-thumb-gray-200">
            {logs.length === 0 && !isProcessing && (
              <div className="text-slate-600 italic m-auto flex flex-col gap-3 items-center">
                <span className="text-sm">Sin actividad todavía...</span>
              </div>
            )}
            {logs.length === 0 && isProcessing && (
              <div className="text-slate-600 italic m-auto flex flex-col gap-3 items-center">
                <div className="w-12 h-12 border-2 border-slate-800 border-t-slate-600 rounded-full animate-spin"></div>
                <span className="text-sm">Sistema listo para monitoreo...</span>
              </div>
            )}

            {logs.map((log) => {
              let colorClass = 'text-blue-600';
              if (log.level === 'warn') colorClass = 'text-amber-600';
              if (log.level === 'error') colorClass = 'text-red-600 font-bold';
              if (log.level === 'success') colorClass = 'text-green-600 font-bold';

              return (
                <div key={log.id} className="mb-2 group/line flex gap-3 hover:bg-slate-800/30 p-1 rounded transition-colors">
                  <span className="text-slate-600 shrink-0 select-none text-[11px] mt-0.5">
                    [{new Date(log.timestamp).toLocaleTimeString()}]
                  </span>
                  <div className="flex flex-col w-full">
                    <span className={`${colorClass} wrap-break-word`}>{log.message}</span>
                    {log.origen?.servidor && (
                      <span
                        title={`Hostname: ${log.origen.hostname ?? '—'}`}
                        className={`mt-0.5 text-[10px] ${
                          // Un log que no viene de producción en el visor de producción merece atención.
                          log.origen.entorno && log.origen.entorno !== 'production' ? 'text-amber-700' : 'text-slate-400'
                        }`}
                      >
                        {[log.origen.servidor, log.origen.entorno, log.origen.version && log.origen.version.slice(0, 7)]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    )}
                    {!!log.payload && (
                      <pre className="mt-1.5 text-[10px] text-gray-500 bg-white p-2.5 rounded-lg border border-gray-200 whitespace-pre-wrap wrap-break-word max-w-full font-mono shadow-inner">
                        {JSON.stringify(log.payload, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              );
            })}

            {hasMore && logs.length > 0 && (
              <div className="py-6 flex justify-center">
                <button
                  onClick={onLoadMore}
                  disabled={isLoadingMore}
                  className="px-4 py-2 text-xs font-bold bg-white border border-gray-200 text-gray-500 rounded-full hover:bg-gray-100 hover:text-gray-900 transition-all shadow-sm active:scale-95 disabled:opacity-50"
                >
                  {isLoadingMore ? (
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                      Cargando historial...
                    </span>
                  ) : (
                    'Cargar más historial'
                  )}
                </button>
              </div>
            )}
            {!hasMore && logs.length > 0 && (
              <div className="py-4 text-center text-xs text-gray-400 italic">Fin del historial registrado</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
